import React from 'react';
import { Helmet } from 'react-helmet';
import { motion } from 'framer-motion';
import Header from '../../components/ui/Header';
import QuickActionButton from '../../components/ui/QuickActionButton';
import PersonalInfoSection from './components/PersonalInfoSection';
import HealthPreferencesSection from './components/HealthPreferencesSection';
import NotificationSettingsSection from './components/NotificationSettingsSection';
import PrivacyDataSection from './components/PrivacyDataSection';
import SecuritySection from './components/SecuritySection';
import ThemeCustomizationSection from './components/ThemeCustomizationSection';
import ConnectedDevicesSection from './components/ConnectedDevicesSection';
import { pageVariants, pageTransition, staggerContainer, fadeUpItem } from '../../components/PageTransition';

const sectionVariants = {
  initial: { opacity: 0, y: 20 },
  animate: (i) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.4, ease: [0.4, 0, 0.2, 1], delay: 0.1 + i * 0.07 },
  }),
};

const settingsSections = [
  { component: PersonalInfoSection, key: 'personal' },
  { component: HealthPreferencesSection, key: 'health' },
  { component: ConnectedDevicesSection, key: 'connected-devices' },
  { component: NotificationSettingsSection, key: 'notifications' },
  { component: ThemeCustomizationSection, key: 'theme' },
  { component: PrivacyDataSection, key: 'privacy' },
  { component: SecuritySection, key: 'security' },
];

const ProfileSettings = () => {
  return (
    <motion.div
      variants={pageVariants}
      initial="initial"
      animate="animate"
      exit="exit"
      transition={pageTransition}
      className="min-h-screen bg-background"
    >
      <Helmet>
        <title>Profile Settings - PrimeOne+</title>
        <meta name="description" content="Manage your PrimeOne+ account settings, health preferences, privacy options, and security settings." />
      </Helmet>
      <Header />
      <main className="container mx-auto px-4 py-8 max-w-6xl">
        {/* Page Header */}
        <motion.div
          variants={fadeUpItem}
          initial="initial"
          animate="animate"
          className="mb-8"
        >
          <h1 className="text-3xl font-bold text-foreground mb-2">Profile Settings</h1>
          <p className="text-muted-foreground">
            Customize your PrimeOne+ experience and manage your account preferences
          </p>
        </motion.div>

        {/* Settings Sections — staggered */}
        <motion.div
          variants={staggerContainer}
          initial="initial"
          animate="animate"
          className="space-y-8"
        >
          {settingsSections?.map(({ component: SectionComponent, key }, i) => {
            const Component = SectionComponent;
            return (
              <motion.div
                key={key}
                custom={i}
                variants={sectionVariants}
                whileHover={{ y: -2, boxShadow: '0 16px 32px rgba(0,0,0,0.07)' }}
                transition={{ duration: 0.2 }}
                className="rounded-2xl"
              >
                <Component />
              </motion.div>
            );
          })}
        </motion.div>

        {/* Footer Note */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.65, duration: 0.4, ease: [0.4, 0, 0.2, 1] }}
          className="mt-12 p-6 bg-muted/30 rounded-xl text-center"
        >
          <p className="text-sm text-muted-foreground">
            Need help with your settings? Visit our{' '}
            <a href="#" className="text-primary hover:underline">Help Center</a>{' '}
            or{' '}
            <a href="#" className="text-primary hover:underline">Contact Support</a>
          </p>
        </motion.div>
      </main>
      <QuickActionButton />
    </motion.div>
  );
};

export default ProfileSettings;